import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';

//values that can be dynamically replaced in message
type Values = Record<string | number, string | number>;

type FormatMessageParams = {
  name?: string;
  id: string;
  defaultMessage?: string;
  values?: Values;
};

//custom hook to be used at top level of components
export const useFormat = (args?: Pick<FormatMessageParams, 'name'>) => {
  //get the translations from the passed file
  const { t: translateById } = useTranslation(args?.name);

  //Main function for translating messages
  const formatMessage = useCallback(
    ({ id, defaultMessage, values = {} }: Omit<FormatMessageParams, 'name'>) => {
      //translate the message using the given id which is a key in the json files
      let translatedMessage = translateById(id);

      //Enhanced fallback for production builds where translations might not load properly
      if (translatedMessage === id || !translatedMessage) {
        // Try to extract a readable fallback from the key
        if (defaultMessage) {
          translatedMessage = defaultMessage;
        } else {
          // Convert key to readable text as last resort
          // e.g., "dashboard.table-headers.invoiced" -> "Invoiced"
          const keyParts = id.split('.');
          const lastPart = keyParts[keyParts.length - 1];
          translatedMessage = lastPart
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        }
      }

      //now replace any replacable dynamic values
      for (const [key, value] of Object.entries(values)) {
        translatedMessage = translatedMessage.replace(new RegExp(`\\{${key}\\}`, 'ig'), `${value}`);
      }

      return translatedMessage;
    },
    [translateById],
  );

  return { formatMessage };
};
